'use strict';

const { SwipeDetector } = require('../../../utils/touch');
const BaseModel = require('../base/BaseModel');
const {
    setTransformStyle,
    setTransitionStyle,
    getTotalPages,
    getPagedItemWidth,
    pageChangeTypeEnum,
    setWidthStyle,
} = require('../utils');
const { noop } = require('../../../mixins/commonjs/basicUtils');

// The transition effect to use when the swipe occurs.
const TRANSITION = 'transform 250ms ease';
// The amount to slow down the dragging effect by when the user reaches the end of the list.
const SLOW_DOWN_FACTOR = 2.5;

function normalizePage(page, totalPages) {
    // If the requested page is less than 0, only go to the first page.
    if (page < 0) {
        page = 0;
    }

    // If the requested page is greater than the last page, only go to the last page.
    if (page >= totalPages) {
        page = totalPages - 1;
    }

    return page;
}

/**
 * View-model for carousels that are swiped through on a touch device.
 * @class
 */
class SwipeModel extends BaseModel {
    constructor({
        itemsPerPage,
        itemWidth,
        itemCount,
        isCentered,
        onPageChange = noop,
        startingPage = 0,
        onStateChange,
    } = {}) {
        super({ onStateChange });

        const totalPages = getTotalPages(itemCount, itemsPerPage);

        Object.assign(this, {
            _itemsPerPage: itemsPerPage,
            _itemCount: itemCount,
            _totalPages: totalPages,
            _onPageChange: onPageChange,
            _isCentered: isCentered,
            // Calculate the width of each item.
            _width: itemWidth || getPagedItemWidth(itemsPerPage),
            _swipeDetector: null,
            _state: {
                page: normalizePage(startingPage, totalPages),
                // Track the position of the items when dragging through the list.
                offset: 0,
                // Track whether we should use the transition effect during a given render.
                transition: true,
                disabled: false,
            },
        });
    }

    enableSwipe() {
        this._state.disabled = false;
    }

    disableSwipe() {
        this._state.disabled = true;
    }

    /**
     * Implement the `_afterMount` method from the `BaseModel`.
     */
    _afterMount() {
        // Add swipe handlers.
        this._swipeDetector = new SwipeDetector({
            onPartialSwipe: this._updateOffset.bind(this),
            onSwipeSuccess: this._swipe.bind(this),
            onSwipeFailure: this._resetOffset.bind(this),
            el: this._el,
        });
    }

    /**
     * Implement the `destroy` method from the `BaseModel`.
     */
    destroy() {
        // The swipe detector adds event listeners so we need to destroy it to prevent memory leaks.
        this._swipeDetector.destroy();
    }

    /**
     * The `_offset` is used to track the "panning" that happens when the user drags the carousel before the full swipe occurs
     *
     * @returns {object}
     */
    _getTransformValue() {
        const fullContainerWidth = 100; // 100% - swipe carousel uses percentages for animations
        let percentage = -this._width * Math.floor(this._itemsPerPage) * this._state.page;

        // isCentered determines whether the carousel items are centered or aligned left
        // when itemWidth prop is set to less than 100
        if (this._isCentered) {
            percentage += (fullContainerWidth - this._width) / 2;
        }

        // Last page
        if (this._isLastPage() && this._itemsPerPage !== 1) {
            percentage = -this._width * this._itemCount + fullContainerWidth;
        }

        if (this._state.offset !== 0) {
            return `calc(${percentage}% + ${this._state.offset}px)`;
        }

        return `${percentage}%`;
    }

    /**
     * Implement the `transformListStyle` method from the `BaseModel`.
     */
    transformListStyle(style) {
        const transformValue = this._getTransformValue();

        setTransformStyle(style, `translateX(${transformValue})`);

        // When the user is just "panning" we don't want to have a transition effect.
        if (this._state.transition) {
            setTransitionStyle(style, TRANSITION);
        }
    }

    /**
     * Implement the `transformItemStyle` method from the `BaseModel`.
     */
    transformItemStyle(style) {
        setWidthStyle(style, this._width);
    }

    /**
     * Update the position of the carousels in response to "drag/pan" by the user.
     * @param {Array} touchCords Array consisting of the touch coordinates [x, y], passed from the `SwipeDetector`.
     * @param {SwipeDirectionType} direction The swipe direction.
     */
    _updateOffset(touchCords, direction) {
        // We don't wanna move the carousel when the user is swiping vertically
        if (this._state.disabled || direction.isUp() || direction.isDown()) {
            return;
        }

        let xOffset = touchCords[0];

        if (
            (this._isLastPage() && direction.isLeft()) ||
            (this._isFirstPage() && direction.isRight())
        ) {
            // If we've reached either end of the list, slow down the dragging effect to indicate to the user that they
            // can't continue.
            xOffset = xOffset / SLOW_DOWN_FACTOR;
        }

        this._state.transition = false;
        this._state.offset = xOffset;
        this._onStateChange();
    }

    /**
     * Call in response to a complete swipe.
     * @param  {SwipeDirectionType} direction The swipe direction.
     */
    _swipe(direction) {
        if (this._state.disabled) {
            return;
        }

        if (direction.isLeft()) {
            this._nextPage();
        } else if (direction.isRight()) {
            this._previousPage();
        }
    }

    /**
     * Unset the "drag" offset and go back to the initial page position.
     */
    _resetOffset() {
        if (this._state.disabled) {
            return;
        }

        this._state.transition = true;
        this._state.offset = 0;

        this._updateState();
    }

    /**
     * Set the new page. Handles bounds issues by going staying on either the first or last page.
     * @param {Number} page The page to go to.
     * @param {PageChangeType} type The type of the page change.
     */
    _setPage(page, type) {
        page = normalizePage(page, this._totalPages);

        // Only update if the page actually changed.
        if (page !== this._state.page) {
            this._state.transition = true;
            this._state.offset = 0;
            const previous = this._state.page;
            this._state.page = page;
            this._onPageChange(page, type, previous);
            this._updateState();
        }
    }

    /**
     * Return the total number of pages.
     * @return {Number}
     */
    getTotalPages() {
        return this._totalPages;
    }

    /**
     * Go to the specified page.
     */
    jumpToPage(page) {
        this._setPage(page, pageChangeTypeEnum.jump());
    }

    /**
     * @returns {boolean}
     */
    canPaginate() {
        return this.getTotalPages() > 1;
    }

    /**
     * Go to the next page.
     */
    _nextPage() {
        // If we are already on the last page, just stay there.
        if (this._isLastPage()) {
            this._resetOffset();
            return;
        }

        this._setPage(this._state.page + 1, pageChangeTypeEnum.next());
    }

    /**
     * Go to the previous page.
     */
    _previousPage() {
        // If we are already on the first page, just stay there.
        if (this._isFirstPage()) {
            this._resetOffset();
            return;
        }

        this._setPage(this._state.page - 1, pageChangeTypeEnum.previous());
    }

    /**
     * Check if we are currently on the first page.
     * @return {Boolean}
     */
    _isFirstPage() {
        return this._state.page === 0;
    }

    /**
     * Check if we are currently on the last page.
     * @return {Boolean}
     */
    _isLastPage() {
        return this._state.page === this._totalPages - 1;
    }
}

module.exports = SwipeModel;



// WEBPACK FOOTER //
// ./assets/js/global/components/carousel/models/SwipeModel.es.js